import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { api } from "@/lib/api";
import { AudioEngine } from "@/lib/AudioEngine";
import { supabase } from "@/lib/supabase";
import type { Episode } from "@/types/episode";
import type { Podcast } from "@/types/podcast";
import debounce from "lodash/debounce";

interface AudioPlayerContextType {
  currentEpisode: Episode | null;
  isPlaying: boolean;
  currentTime: number;
  feedEpisodes: Episode[];
  allEpisodes: Episode[];
  duration: number;
  error: Error | null;
  audioState: string;
  queueEpisodes: Episode[];
  historyEpisodes: Episode[];
  likedEpisodes: Episode[];
  dislikedEpisodes: Episode[];
  bookmarkedEpisodes: Episode[];
  listenHistory: Episode[];
  displayedEpisodes: Episode[];
  isFavorited: boolean;
  isDisliked: boolean;
  setQueueEpisodes: (
    episodes: Episode[] | ((prev: Episode[]) => Episode[]),
  ) => void;
  setFeedEpisodes: (episodes: Episode[]) => void;
  setAllEpisodes: (episodes: Episode[]) => void;
  playEpisode: (episode: Episode, isAuto: boolean, options?: { skipHistoryRecord?: boolean; resetHistory?: boolean }) => void;
  setListenHistory: (episodes: Episode[]) => void;
  setHistoryEpisodes: (episodes: Episode[]) => void;
  setDisplayedEpisodes: (episodes: Episode[]) => void;
  togglePlay: () => void;
  seek: (time: number[]) => void;
  toggleBookmark: (episodeId: string) => Promise<void>;
  isEpisodeBookmarked: (episodeId: string) => boolean;
  toggleFavorite: () => Promise<void>;
  toggleDislike: () => Promise<void>;
  isEpisodeLiked: (episodeId: string) => boolean;
  setOnEndedCallback: (callback: () => void) => void;
  retry: () => Promise<void>;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | null>(null);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error(
      "useAudioPlayer must be used within an AudioPlayerProvider",
    );
  }
  return context;
};

export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentEpisode, setCurrentEpisode] = useState<Episode | null>(null);
  const currentEpisodeRef = useRef<Episode | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [feedEpisodes, setFeedEpisodes] = useState<Episode[]>([]);
  const [allEpisodes, setAllEpisodesInternal] = useState<Episode[]>([]);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [historyEpisodes, setHistoryEpisodes] = useState<Episode[]>([]);
  const [queueEpisodes, setQueueEpisodesInternal] = useState<Episode[]>([]);
  const [displayedEpisodes, setDisplayedEpisodesInternal] = useState<Episode[]>(
    [],
  );
  const [audioState, setAudioState] = useState("idle");
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playedEpisodeIds, setPlayedEpisodeIds] = useState(new Set<string>());

  const audioEngineRef = useRef<AudioEngine | null>(null);
  const onEndedCallbackRef = useRef<(() => void) | null>(null);
  const listenTimeoutRef = useRef<NodeJS.Timeout>();
  const { user } = useAuth();

  // Custom hook for episode state management
  const useEpisodeState = () => {
    const [episodes, setEpisodes] = useState<Episode[]>([]);

    const updateEpisodeMetrics = useCallback(
      (episodeId: string, metrics: { likes?: number; listens?: number }) => {
        setEpisodes((prev) =>
          prev.map((ep) =>
            ep.id === episodeId
              ? {
                  ...ep,
                  likes: metrics.likes ?? ep.likes,
                  listens: metrics.listens ?? ep.listens,
                }
              : ep,
          ),
        );
      },
      [],
    );

    return { episodes, setEpisodes, updateEpisodeMetrics };
  };

  // Episode lists state using the custom hook
  const {
    episodes: likedEpisodes,
    setEpisodes: setLikedEpisodes,
    updateEpisodeMetrics: updateLikedEpisodeMetrics,
  } = useEpisodeState();

  const {
    episodes: dislikedEpisodes,
    setEpisodes: setDislikedEpisodes,
    updateEpisodeMetrics: updateDislikedEpisodeMetrics,
  } = useEpisodeState();

  const {
    episodes: bookmarkedEpisodes,
    setEpisodes: setBookmarkedEpisodes,
    updateEpisodeMetrics: updateBookmarkedEpisodeMetrics,
  } = useEpisodeState();

  const { episodes: listenHistory, setEpisodes: setListenHistoryInternal } =
    useEpisodeState();

  const setQueueEpisodes = useCallback(
    (episodes: Episode[] | ((prev: Episode[]) => Episode[])) => {
      setQueueEpisodesInternal(episodes);
      // Update displayed episodes to match the new queue
      if (typeof episodes === "function") {
        setDisplayedEpisodesInternal((prev) => {
          const newQueue = episodes(queueEpisodes);
          return newQueue;
        });
      } else {
        setDisplayedEpisodesInternal(episodes);
      }
    },
    [queueEpisodes],
  );

  const setDisplayedEpisodes = useCallback((episodes: Episode[]) => {
    setDisplayedEpisodesInternal(episodes);
  }, [displayedEpisodes]);

  const setAllEpisodes = useCallback((episodes: Episode[]) => {
    console.log('Setting all episodes. Total count:', episodes.length);
    console.log('First 30 episodes:', episodes.slice(0, 30).map(ep => ({
      id: ep.id,
      title: ep.title,
      podcastId: ep.podcastId
    })));
    setAllEpisodesInternal(episodes);
  }, []);

  const playEpisode = useCallback(
    async (episode: Episode, isAuto: boolean = false, options?: { skipHistoryRecord?: boolean; resetHistory?: boolean }) => {
      if (!audioEngineRef.current) return;

      try {
        // Clear any existing timeout first, before changing episodes
        if (listenTimeoutRef.current) {
          clearTimeout(listenTimeoutRef.current);
          listenTimeoutRef.current = undefined;
        }

        setIsAutoPlaying(isAuto);

        // Add current episode to history and mark as played, unless skipHistoryRecord is true
        if (currentEpisodeRef.current && !options?.skipHistoryRecord) {
          if (options?.resetHistory) {
            // Reset history if requested (manual play)
            setHistoryEpisodes([currentEpisodeRef.current]);
          } else {
            // Append to history (auto-play)
            setHistoryEpisodes((prev) => 
              [currentEpisodeRef.current!, ...prev].slice(0, 50)
            );
          }
        }

        // Mark the new episode as played immediately
        setPlayedEpisodeIds(prev => {
          console.log('Adding to played episodes:', episode.title);
          return new Set(prev).add(episode.id);
        });

        currentEpisodeRef.current = episode;
        setCurrentEpisode(episode);

        // Set up listen timer before starting playback
        if (user && !options?.skipHistoryRecord) {
          const episodeToTrack = episode; // Capture the episode in closure
          listenTimeoutRef.current = setTimeout(async () => {
            try {
              // Check if this is still the current episode using the ref
              if (currentEpisodeRef.current?.id !== episodeToTrack.id) {
                return;
              }
              await api.episodes.recordListen(
                user.id,
                episodeToTrack.id,
                episodeToTrack.podcastId,
              );
            } catch (error) {
              console.error("Error recording listen:", error);
            }
          }, 10000);
        }

        // Start playback after setting up timer
        await audioEngineRef.current.loadAndPlay(episode);
        setIsPlaying(true);
        setError(null);

        // Remove the episode from both queue and displayed episodes immediately
        setQueueEpisodesInternal((prev) =>
          prev.filter((ep) => ep.id !== episode.id),
        );
        setDisplayedEpisodesInternal((prev) =>
          prev.filter((ep) => ep.id !== episode.id),
        );

        if ("mediaSession" in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: episode.title,
            artist: episode.podcastName,
            artwork: [
              { src: episode.imageUrl, sizes: "512x512", type: "image/jpeg" },
            ],
          });
        }
      } catch (error) {
        // Clear any existing timeout on error to prevent duplicate listen counts
        if (listenTimeoutRef.current) {
          clearTimeout(listenTimeoutRef.current);
          listenTimeoutRef.current = undefined;
        }
        console.error("Failed to play episode:", error);
        setError(error as Error);
        setIsPlaying(false);
      }
    },
    [
      user,
      audioEngineRef,
      currentEpisodeRef,
      setHistoryEpisodes,
      listenTimeoutRef,
    ],
  );

  // Simplified episode interaction handlers
  const handleEpisodeAction = useCallback(
    async (
      action: "like" | "dislike" | "bookmark",
      episodeId: string,
      add: boolean,
    ) => {
      if (!user || !episodeId) return;

      try {
        const apiActions = {
          like: { add: api.episodes.like, remove: api.episodes.unlike },
          dislike: {
            add: api.episodes.dislike,
            remove: api.episodes.undislike,
          },
          bookmark: {
            add: api.episodes.bookmark,
            remove: api.episodes.unbookmark,
          },
        };

        const stateSetter = {
          like: setLikedEpisodes,
          dislike: setDislikedEpisodes,
          bookmark: setBookmarkedEpisodes,
        };

        if (add) {
          // Look for the episode in all possible sources
          const episode =
            currentEpisode?.id === episodeId
              ? currentEpisode
              : [...queueEpisodes, ...feedEpisodes, ...likedEpisodes].find(
                  (ep) => ep.id === episodeId,
                );

          if (!episode) return;

          await apiActions[action].add(user.id, episodeId, episode.podcastId);
          stateSetter[action]((prev) => [episode, ...prev]);
        } else {
          await apiActions[action].remove(user.id, episodeId);
          stateSetter[action]((prev) =>
            prev.filter((ep) => ep.id !== episodeId),
          );
        }
      } catch (error) {
        throw error;
      }
    },
    [user, currentEpisode, queueEpisodes, feedEpisodes, likedEpisodes],
  );

  const toggleFavorite = useCallback(async () => {
    if (!currentEpisode) return;
    const isLiked =
      likedEpisodes?.some((ep) => ep.id === currentEpisode.id) || false;
    await handleEpisodeAction("like", currentEpisode.id, !isLiked);
  }, [currentEpisode, likedEpisodes, handleEpisodeAction]);

  const toggleDislike = useCallback(async () => {
    if (!currentEpisode) return;
    const isDisliked =
      dislikedEpisodes?.some((ep) => ep.id === currentEpisode.id) || false;
    await handleEpisodeAction("dislike", currentEpisode.id, !isDisliked);
  }, [currentEpisode, dislikedEpisodes, handleEpisodeAction]);

  const toggleBookmark = useCallback(
    async (episodeId: string) => {
      const isBookmarked =
        bookmarkedEpisodes?.some((ep) => ep.id === episodeId) || false;
      await handleEpisodeAction("bookmark", episodeId, !isBookmarked);
    },
    [bookmarkedEpisodes, handleEpisodeAction],
  );

  const togglePlay = useCallback(async () => {
    if (!audioEngineRef.current || !currentEpisode) return;

    try {
      if (isPlaying) {
        audioEngineRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioEngineRef.current.play();
        setIsPlaying(true);
        setError(null);
      }
    } catch (error) {
      setError(error as Error);
      setIsPlaying(false);
    }
  }, [isPlaying, currentEpisode]);

  const seek = useCallback((time: number[]) => {
    if (!audioEngineRef.current) return;
    audioEngineRef.current.seek(time[0]);
  }, []);

  const retry = useCallback(async () => {
    if (!currentEpisode) return;
    try {
      await playEpisode(currentEpisode);
      setError(null);
    } catch (error) {
      setError(error as Error);
    }
  }, [currentEpisode, playEpisode]);

  const setOnEndedCallback = useCallback((callback: () => void) => {
    onEndedCallbackRef.current = callback;
  }, []);

  const debouncedUpdateProgress = useCallback(
    debounce(
      (
        userId: string,
        episodeId: string,
        podcastId: string,
        progress: number,
      ) => {
        api.episodes
          .updateProgress(userId, episodeId, podcastId, progress)
          .catch((error) => {
            setError(error as Error);
          });
      },
      2000,
    ),
    [],
  );

  const isEpisodeLiked = useCallback(
    (episodeId: string) => {
      return likedEpisodes?.some?.((ep) => ep.id === episodeId) || false;
    },
    [likedEpisodes],
  );

  const isEpisodeBookmarked = useCallback(
    (episodeId: string) => {
      return bookmarkedEpisodes?.some?.((ep) => ep.id === episodeId) || false;
    },
    [bookmarkedEpisodes],
  );

  // Simplified realtime metrics subscription
  useEffect(() => {
    if (!currentEpisode) return;

    const handleMetricsUpdate = (metrics: any) => {
      if (!metrics) return;

      const updatedMetrics = {
        likes: metrics.like_count || 0,
        listens: metrics.listen_count || 0,
      };

      setCurrentEpisode((prev) =>
        prev ? { ...prev, ...updatedMetrics } : null,
      );

      // Update metrics in all episode lists
      [
        updateLikedEpisodeMetrics,
        updateDislikedEpisodeMetrics,
        updateBookmarkedEpisodeMetrics,
      ].forEach((updater) => updater(currentEpisode.id, updatedMetrics));
    };

    const channel = supabase
      .channel(`episode_metrics_${currentEpisode.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "episode_metrics",
          filter: `episode_id=eq.${currentEpisode.id}`,
        },
        (payload) => {
          try {
            handleMetricsUpdate(payload.new);
          } catch (error) {
            console.error("Error updating metrics:", error);
          }
        },
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [currentEpisode?.id]);

  // Load user data
  useEffect(() => {
    if (user) {
      Promise.all([
        api.episodes.getLiked(user.id),
        api.episodes.getDisliked(user.id),
        api.episodes.getBookmarked(user.id),
        api.episodes.getListenHistory(user.id),
      ])
        .then(([likedData, dislikedData, bookmarkedData, historyData]) => {
          setLikedEpisodes(likedData || []);
          setDislikedEpisodes(dislikedData || []);
          setBookmarkedEpisodes(bookmarkedData || []);
          setListenHistoryInternal(historyData?.episodes || []);
        })
        .catch(console.error);
    }
  }, [user]);

  // Load initial listen history
  useEffect(() => {
    const loadInitialHistory = async () => {
      if (!user) return;
      try {
        const { episodes } = await api.episodes.getListenHistory(
          user.id,
          1,
          20,
        );
        setListenHistoryInternal(episodes);
      } catch (error) {
        console.error("Error loading initial listen history:", error);
      }
    };
    loadInitialHistory();
  }, [user]);

  // Update listen history when current time changes
  useEffect(() => {
    if (!user || !currentEpisode || currentTime <= 0) return;

    // Debounce the progress updates to avoid too many calls
    const progress = currentTime / duration;
    const updateProgressDebounced = debounce(async () => {
      try {
        await api.episodes.updateProgress(
          user.id,
          currentEpisode.id,
          currentEpisode.podcastId,
          progress,
        );
        
        // Update local state
        setListenHistoryInternal((prev) => [
          { ...currentEpisode, progress },
          ...prev.filter((ep) => ep.id !== currentEpisode.id),
        ]);
      } catch (error) {
        setError(error as Error);
      }
    }, 2000); // Only update every 2 seconds at most

    updateProgressDebounced();

    return () => {
      updateProgressDebounced.cancel();
    };
  }, [user, currentEpisode, currentTime, duration]);

  // Auto-extend queue when it gets too small
  useEffect(() => {
    if (queueEpisodes.length < 3 && allEpisodes.length > currentIndex) {
      console.log('Queue size below threshold, extending queue...');
      console.log('Current index:', currentIndex);
      console.log('Current played episodes:', Array.from(playedEpisodeIds));
      
      // Get current queue IDs to avoid duplicates
      const currentQueueIds = new Set(queueEpisodes.map(ep => ep.id));
      
      // Find next unplayed episodes
      const nextEpisodes = allEpisodes
        .slice(currentIndex)
        .filter(ep => !playedEpisodeIds.has(ep.id) && !currentQueueIds.has(ep.id))
        .slice(0, 7);

      console.log('Adding episodes:', nextEpisodes.map(ep => ep.title));

      if (nextEpisodes.length > 0) {
        setQueueEpisodesInternal(prev => {
          const newQueue = [...prev, ...nextEpisodes];
          console.log('New queue:', newQueue.map(ep => ep.title));
          return newQueue;
        });
        setDisplayedEpisodesInternal(prev => {
          const newDisplay = [...prev, ...nextEpisodes];
          console.log('New display:', newDisplay.map(ep => ep.title));
          return newDisplay;
        });
        setCurrentIndex(currentIndex + nextEpisodes.length);
      } else if (currentIndex < allEpisodes.length) {
        // If we didn't find any unplayed episodes in the current slice,
        // but there are more episodes available, move the index forward
        setCurrentIndex(Math.min(currentIndex + 7, allEpisodes.length));
      }
    }
  }, [queueEpisodes.length, allEpisodes, currentIndex, playedEpisodeIds]);

  // Reset pagination when all episodes are updated
  useEffect(() => {
    setCurrentIndex(0);
    setPlayedEpisodeIds(new Set());
  }, [allEpisodes]);

  // Initialize AudioEngine
  useEffect(() => {
    audioEngineRef.current = new AudioEngine({
      onTimeUpdate: setCurrentTime,
      onDurationChange: setDuration,
      onError: setError,
      onEnded: () => {
        setIsPlaying(false);
        if (onEndedCallbackRef.current) {
          onEndedCallbackRef.current();
        }
        setIsAutoPlaying(false);
      },
      onStateChange: setAudioState,
    });

    return () => {
      if (audioEngineRef.current) {
        audioEngineRef.current.cleanup();
      }
    };
  }, []);

  useEffect(() => {
    if ('mediaSession' in navigator) {
      const updateMediaSession = () => {
        if (!currentEpisode) return;

        const artwork = currentEpisode.imageUrl 
          ? [{ src: currentEpisode.imageUrl, sizes: '512x512', type: 'image/jpeg' }]
          : [];

        navigator.mediaSession.metadata = new MediaMetadata({
          title: currentEpisode.title,
          artist: currentEpisode.podcastTitle || 'Podcast',
          album: currentEpisode.podcastTitle || 'Podcast',
          artwork: artwork
        });

        // Always set play/pause handlers to maintain session
        navigator.mediaSession.setActionHandler('play', () => {
          if (!isPlaying) togglePlay();
        });

        navigator.mediaSession.setActionHandler('pause', () => {
          if (isPlaying) togglePlay();
        });

        // Implement media session action handlers
        navigator.mediaSession.setActionHandler('previoustrack', () => {
          console.log("previoustrack handler invoked");
          console.log("Current queueEpisodes:", queueEpisodes);
          console.log("Current historyEpisodes:", historyEpisodes);

          if (currentTime > 3) {
            console.log("Condition met: currentTime > 3. Seeking to beginning of current episode.");
            seek([0]);
            return;
          }

          if (historyEpisodes.length > 0) {
            console.log("Condition met: historyEpisodes not empty. Proceeding to play previous episode.");
            const [prevEpisode, ...remainingHistory] = historyEpisodes;
            console.log("Previous episode:", prevEpisode);
            console.log("Remaining historyEpisodes after removal:", remainingHistory);

            setHistoryEpisodes(remainingHistory);

            if (currentEpisode) {
              setQueueEpisodes((prev) => [currentEpisode, ...prev]);
              console.log("Added currentEpisode to queueEpisodes:", currentEpisode);
            }

            playEpisode(prevEpisode, false);
          } else if (currentTime > 0) {
            console.log("Condition met: no historyEpisodes but currentTime > 0. Seeking to beginning of current episode.");
            seek([0]);
          }
        });

        navigator.mediaSession.setActionHandler('nexttrack', () => {
          if (queueEpisodes.length > 0) {
            const currentIndex = queueEpisodes.findIndex(ep => ep.id === currentEpisode.id);
            if (currentIndex < queueEpisodes.length - 1) {
              playEpisode(queueEpisodes[currentIndex + 1]);
            }
          }
        });

        // Update playback state
        if (isPlaying) {
          navigator.mediaSession.playbackState = 'playing';
        } else {
          navigator.mediaSession.playbackState = 'paused';
        }

        // Prevent default seek behaviors
        navigator.mediaSession.setActionHandler('seekbackward', null);
        navigator.mediaSession.setActionHandler('seekforward', null);
      };

      updateMediaSession();
    }
  }, [currentEpisode, queueEpisodes, playEpisode, isPlaying, togglePlay, currentTime]);

  const contextValue = {
    currentEpisode,
    isPlaying,
    currentTime,
    feedEpisodes,
    allEpisodes,
    duration,
    error,
    audioState,
    queueEpisodes,
    historyEpisodes,
    likedEpisodes,
    dislikedEpisodes,
    bookmarkedEpisodes,
    listenHistory,
    displayedEpisodes,
    isFavorited: currentEpisode
      ? likedEpisodes?.some?.((ep) => ep.id === currentEpisode.id) || false
      : false,
    isDisliked: currentEpisode
      ? dislikedEpisodes?.some?.((ep) => ep.id === currentEpisode.id) || false
      : false,
    setQueueEpisodes,
    setFeedEpisodes,
    setAllEpisodes,
    setListenHistory: setListenHistoryInternal,
    setHistoryEpisodes,
    setDisplayedEpisodes,
    playEpisode,
    togglePlay,
    seek,
    toggleBookmark,
    isEpisodeBookmarked,
    toggleFavorite,
    toggleDislike,
    isEpisodeLiked,
    setOnEndedCallback,
    retry,
  };

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      {children}
    </AudioPlayerContext.Provider>
  );
};
